import { IProjectileContainer } from "../IProjectileContainer";
import { DigOutState } from "./states/DigOutState";
import { GameResourcesPackage } from "../../../../resources/GameResourcesPackage";
import { AudioController } from "../../../AudioController";
import { FreezeState } from "./states/FreezeState";
import { BaseMole } from "./BaseMole";
import { Vegetable } from "../Vegetable";
import { HatType } from "../../../../models/data/HatType";
import { Character } from "../../character/Character";
import { BatchedRenderer } from "three.quarks";
import { GameController } from "../../../../controllers/GameController";
import { TutorialIdleState } from "./states/TutorialIdleState";
import { Sprite, SpriteMaterial, Vector3 } from "three";
import { Ticker } from "pixi.js";
import { TutorialDieState } from "./states/TutorialDieState";
import { PortalText } from "../portal/PortalText";
import { FontResourcesPackage } from "../../../../../../common/resources/FontResourcesPackage";
import { LocalizationModel } from "../../../../../../common/models/LocalizationModel";
import { LocalizationKey } from "../../../../../../localization/Localization";

export class TutorialMole extends BaseMole {
    protected arrow: Sprite;
    protected textEffect: PortalText;
    protected arrowAnimationUnsubscribe: () => void;
    public constructor(
        gameResources: GameResourcesPackage,
        protected readonly fontResources: FontResourcesPackage,
        protected readonly localization: LocalizationModel,
        projectileContainer: IProjectileContainer,
        particlesRenderer: BatchedRenderer,
        character: Character,
        _vegetable: Vegetable,
        audioController: AudioController,
        gameController: GameController,
        fieldIndex: number
    ) {
        super(
            fieldIndex,
            gameResources,
            projectileContainer,
            particlesRenderer,
            character,
            audioController,
            gameController
        );

        const material = new SpriteMaterial({
            map: gameResources.assets_game_tutorial_arrow_thpng,
            depthWrite: false
        });
        this.arrow = new Sprite(material);
        this.arrow.position.set(0, 2.2, 0);

        const dieState = new TutorialDieState(this, this.animationController, this.gameController);
        const freezeState = new FreezeState(gameResources, particlesRenderer, this, this.animationController, dieState);
        const tutorialState = new TutorialIdleState(this, this.animationController, dieState, freezeState, this.randomIdleAnimationTimeout());
        const digOutState = new DigOutState(this, this.animationController, tutorialState, dieState, freezeState);
        this.arrowAnimationUnsubscribe = this.arrowAnimation(new Vector3(0, 3.2, 0), new Vector3(0, 2.2, 0));
        this.switchState(digOutState);
    }

    public override onAddedToStage() {
        this.parent.add(this.arrow);
        this.textEffect = new PortalText(this.fontResources, this.localization, LocalizationKey.Click);
        this.textEffect.position.set(0, 3.2, 0);
        this.parent.add(this.textEffect);
        super.onAddedToStage();
    }

    protected arrowAnimation(startPosition: Vector3, endPosition: Vector3, durationMS: number = 1000) {
        let elapsed = 0;
        let forward = true;

        const update = (delta: number) => {
            // delta is a multiplier (1 = 60fps), so convert to ms
            elapsed += delta * (1000 / 60);

            // normalize progress [0..1]
            let progress = elapsed / durationMS;
            if (progress > 1) progress = 1;

            // interpolate between A and B
            if (forward) {
                this.arrow.position.x = startPosition.x + (endPosition.x - startPosition.x) * progress;
                this.arrow.position.y = startPosition.y + (endPosition.y - startPosition.y) * progress;
                this.arrow.position.z = startPosition.z + (endPosition.z - startPosition.z) * progress;
            } else {
                this.arrow.position.x = endPosition.x + (startPosition.x - endPosition.x) * progress;
                this.arrow.position.y = endPosition.y + (startPosition.y - endPosition.y) * progress;
                this.arrow.position.z = endPosition.z + (startPosition.z - endPosition.z) * progress;
            }

            // when finished one way → swap direction
            if (progress >= 1) {
                forward = !forward;
                elapsed = 0;
            }
        };

        Ticker.shared.add(update, this);

        return () => Ticker.shared.remove(update, this);
    }

    protected override resolveHatType(): HatType {
        return HatType.Cap;
    }

    public cancelArrowAnimation(): void {
        this.arrowAnimationUnsubscribe && this.arrowAnimationUnsubscribe();
        this.arrow.visible = false;
    }

    public cancelTextAnimation(): void {
        this.textEffect.destroy();
    }

    public override destroy(): void {
        this.arrowAnimationUnsubscribe && this.arrowAnimationUnsubscribe();
        super.destroy();
    }
}